import { gql, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import * as Yup from 'yup';

import { GraphRef, graphRefToString } from 'src/app/graph/hooks/useGraphRef';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  UseUpsertCollectionValidationQuery,
  UseUpsertCollectionValidationQueryVariables,
  UseUpsertSandboxCollectionValidationQuery,
  UseUpsertSandboxCollectionValidationQueryVariables,
  UserPermission,
} from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

export const UseCollectionNameValidationCollectionFragment = gql`
  fragment UseCollectionNameValidationCollectionFragment on OperationCollection {
    id
    isShared
    isSandbox
    name
    variants {
      id
    }
  }
`;

export function useCollectionNameValidation({
  graphRef: inputGraphRef,
  isShared: inputIsShared,
  collection,
  loading: collectionLoading,
}:
  | { graphRef: GraphRef; isShared: true; collection?: never; loading?: never }
  | {
      graphRef: GraphRef | null;
      isShared: false;
      collection?: never;
      loading?: never;
    }
  | {
      graphRef?: never;
      isShared?: never;
      collection:
        | GraphQLTypes.UseCollectionNameValidationCollectionFragment
        | undefined;
      loading: boolean;
    }) {
  const graphRef =
    inputGraphRef !== undefined
      ? graphRefToString(inputGraphRef)
      : !collectionLoading
        ? collection?.isSandbox
          ? null
          : collection && isMinLengthArray(1, collection?.variants)
            ? collection.variants[0].id
            : undefined // collection is not a sandbox collection, but has no variants, this shouldn't happen
        : undefined; // collection is loading, we don't know the graphRef yet

  const isShared = collection ? collection.isShared : inputIsShared;
  const registeredCollectionNamesResult = useQuery(
    gql<
      UseUpsertCollectionValidationQuery,
      UseUpsertCollectionValidationQueryVariables
    >`
      query UseUpsertCollectionValidationQuery($ref: ID!) {
        variant(ref: $ref) {
          ... on GraphVariant {
            id
            operationCollections {
              id
              name
              isShared
            }
          }
        }
      }
    `,
    { variables: { ref: graphRef as string }, skip: !graphRef },
  );
  const sandboxCollectionNamesResult = useQuery(
    gql<
      UseUpsertSandboxCollectionValidationQuery,
      UseUpsertSandboxCollectionValidationQueryVariables
    >`
      query UseUpsertSandboxCollectionValidationQuery {
        me {
          id
          ... on User {
            sandboxOperationCollections {
              id
              name
            }
          }
        }
      }
    `,
    { variables: {}, skip: graphRef === null },
  );

  const existingCollectionNames = useMemo(() => {
    if (graphRef) {
      const variant = registeredCollectionNamesResult.data?.variant;
      if (variant?.__typename === 'GraphVariant') {
        return variant.operationCollections
          .filter((c) => c.id !== collection?.id && isShared === c.isShared)
          .map((c) => c.name);
      }
    } else if (graphRef === null) {
      const me = sandboxCollectionNamesResult.data?.me;
      if (me?.__typename === 'User') {
        return me.sandboxOperationCollections
          .filter((c) => c.id !== collection?.id)
          .map((c) => c.name);
      }
    }
    return [];
  }, [
    collection?.id,
    graphRef,
    isShared,
    registeredCollectionNamesResult.data?.variant,
    sandboxCollectionNamesResult.data?.me,
  ]);

  return {
    loading:
      registeredCollectionNamesResult.loading ||
      sandboxCollectionNamesResult.loading ||
      collectionLoading,
    existingCollectionNames,
    collectionNameValidation: useMemo(
      () =>
        Yup.string()
          .trim()
          .required('Collection name is required')
          .max(100, 'Must be at most 100 characters')
          .notOneOf(existingCollectionNames, (value) => {
            return value.value === collection?.name
              ? `A recent update to Explorer requires collection names to be unique moving forward. Since a ${
                  !graphRef ? 'sandbox' : isShared ? 'shared' : 'personal'
                } collection with this name already exists, please use a different name.`
              : `Another ${
                  !graphRef ? 'sandbox' : isShared ? 'shared' : 'personal'
                } collection with this name already exists. Please try a different name.`;
          }),
      [existingCollectionNames, graphRef, isShared, collection],
    ),
  };
}

export function useUpsertCollectionValidation(
  ...args: Parameters<typeof useCollectionNameValidation>
) {
  const { collectionNameValidation, loading } = useCollectionNameValidation(
    ...args,
  );
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: collectionNameValidation,
        description: collectionDescriptionValidation,
        minEditRole: Yup.mixed<UserPermission>()
          .oneOf([null, ...Object.values(UserPermission)])
          .nullable(),
      }),
    [collectionNameValidation],
  );

  return { validationSchema, loading };
}

export const collectionDescriptionValidation = Yup.string()
  .nullable()
  .trim()
  .max(1000, 'Must be at most 1000 characters');

export const NEW_DEFAULT_COLLECTION_OPTION_ID = 'new-default-collection';
export const useCollectionEntryNameValidation = ({
  collectionIdOrNew,
  collectionEntryId,
}: {
  collectionIdOrNew: string;
  collectionEntryId?: string;
}) => {
  const { data, loading } = useQuery<
    GraphQLTypes.UseCollectionEntryNameValidation,
    GraphQLTypes.UseCollectionEntryNameValidationVariables
  >(
    gql`
      query UseCollectionEntryNameValidation($collectionId: ID!) {
        operationCollection(id: $collectionId) {
          ... on OperationCollection {
            id
            isShared
            isSandbox
            name
            operations {
              id
              name
            }
          }
        }
      }
    `,
    {
      variables: { collectionId: collectionIdOrNew },
      skip: collectionIdOrNew === NEW_DEFAULT_COLLECTION_OPTION_ID,
    },
  );

  const collectionEntryNameValidation = useMemo(() => {
    const validation = Yup.string()
      .matches(
        /^[A-Za-z][A-Za-z0-9_]*$/,
        'Name can contain alphanumeric and underscore characters only and must start with a letter.',
      )
      .required('Operation name is required')
      .max(100, 'Must be at most 100 characters');

    if (data?.operationCollection.__typename === 'OperationCollection') {
      return validation.notOneOf(
        data.operationCollection.operations
          .filter((o) => o.id !== collectionEntryId)
          .map((o) => o.name),
        `Another operation with this name already exists in ${
          data.operationCollection.isSandbox
            ? 'Sandbox'
            : data.operationCollection.isShared
              ? 'Shared'
              : 'Personal'
        }/${data.operationCollection.name}. Please try a different name.`,
      );
    } else {
      return validation;
    }
  }, [collectionEntryId, data?.operationCollection]);
  return { collectionEntryNameValidation, loading };
};

export const editRolesValidationSchemaFragment = Yup.array(
  Yup.mixed<UserPermission>().oneOf(Object.values(UserPermission)),
);
